import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { sortBy, SortOrder, takeUntilDestroy } from '@frontend-site/shared/utils';
import * as _ from 'lodash';
import { tap } from 'rxjs/operators';
import { KeyboardService } from '../../keyboard';
import { PFieldAbstractComponent } from '../p-field-abstract';
import { PFieldSingleSelectService, SelectedValueChangeInfo } from './p-field-single-select.service';
import { IconName } from '@panaya/ui/p-icon';
import { ColorName } from '@panaya/ui/styles-to-code';

@Component({
    selector: 'p-field-single-select',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [isDisable]="isDisabled"
            [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
            [style]="dynamicStyle"
            [actionIconName]="actionIconName"
            [actionIconUrl]="actionIconUrl"
        >
            <div slot="readMode">
                <mat-form-field
                    class="p-input-text p-field-single-select"
                    appearance="fill"
                    [class.p-input-text-readonly]="isReadOnly"
                    [matTooltip]="valueTooltip"
                    matTooltipPosition="above"
                >
                    <input
                        [attr.automation-id]="'p-field-single-select_' + getAutomationSuffix()"
                        matInput
                        [value]="value | extract: getValue:list"
                        class="p-input-text-input"
                        [ngClass]="{ input_with_icon: shouldDisplayIcon() }"
                        [attr.aria-label]="ariaLabel"
                        readonly
                        [disabled]="isDisabled"
                        [matTooltip]="value | extract: getValue:list"
                        [matTooltipDisabled]="emptyValue || !!valueTooltip || hideToolTip"
                    />
                    <p-icon *ngIf="shouldDisplayIcon()" name="map-pin" size="md"></p-icon>
                    <ng-content select="[slot=readMode]" fxFlex="20"></ng-content>
                </mat-form-field>
            </div>

            <div
                slot="editMode"
                [mat-menu-trigger-for]="optionsMenu"
                [ngClass]="isReadOnlyGray ? 'read-only-gray' : ''"
                (menuClosed)="listOptionClosed.emit()"
            >
                <ng-container
                    *ngTemplateOutlet="
                        triggerTmpl || renderTriggerAs == 'button' ? buttonTrigger : fieldTrigger;
                        context: { $implicit: (value | extract: getValue:list) }
                    "
                >
                </ng-container>
                <ng-content select="[slot=editMode]" fxFlex="20"></ng-content>
            </div>
        </p-field>

        <mat-menu
            #optionsMenu="matMenu"
            (click)="$event.stopPropagation()"
            [class]="menuCustomClasses + ' no-padding embedded p-menu p-menu-single'"
        >
            <ng-template matMenuContent>
                <div fxLayout="column" (click)="$event.stopImmediatePropagation()">
                    <p-picker-single-select
                        [style]="menuDynamicStyle"
                        [emptyOption]="emptyOption"
                        [autoFocus]="true"
                        [list]="list"
                        [idField]="idField"
                        [nameField]="nameField"
                        [value]="value"
                        (valueChange)="changeValue($event)"
                        [canSearch]="canSearch"
                        (searchChanged)="searchChanged.emit($event)"
                        [noData]="noData"
                        placeholder="search.."
                        customClasses="no-padding"
                    >
                    </p-picker-single-select>
                </div>
            </ng-template>
        </mat-menu>

        <ng-template #fieldTrigger let-value>
            <mat-form-field class=" p-input-text" appearance="fill">
                <input
                    [attr.automation-id]="'p-field-single-select_' + getAutomationSuffix()"
                    matInput
                    readonly
                    [ngClass]="(customClasses || '') + ' ' + (shouldDisplayIcon() ? 'input_with_icon' : '')"
                    type="text"
                    class="p-input-text-input ellipsis"
                    [placeholder]="placeholder | sentenceCase"
                    [value]="value"
                    [matTooltip]="value"
                    [matTooltipDisabled]="emptyValue || hideToolTip"
                    (keydown)="onKeydownEvent($event)"
                />
                <p-icon *ngIf="shouldDisplayIcon()" name="map-pin" size="md"></p-icon>
                <span *ngIf="iconTemplate"><ng-container *ngTemplateOutlet="iconTemplate"></ng-container></span>
                <p-icon class="p-field-input-icon" name="chevron-down" size="sm"></p-icon>
            </mat-form-field>
        </ng-template>

        <ng-template #buttonTrigger let-value>
            <p-button
                [attr.automation-id]="'p-field-single-select_' + getAutomationSuffix()"
                class="p-menu-trigger-button"
                iconAfter="chevron-down"
                iconAfterSize="md"
                [label]="value || placeholder"
                [buttonType]="'tertiary'"
            ></p-button>
        </ng-template>
    `,
    styleUrls: ['./p-field-single-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldSingleSelectComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldSingleSelect'
})
export class PFieldSingleSelectComponent
    extends PFieldAbstractComponent<number | string | null>
    implements AfterViewInit, OnInit, OnChanges
{
    @Input() field?: any;
    @Input() label?: string;
    @Input() list: ListItem[] = [];
    @Input() supportCurrentIcon = false;

    @Input() canSearch = false;
    @Input() isMandatory = false;

    @Input() customClasses?: string = '';
    @Input() menuCustomClasses?: string = '';
    @Input() isReadOnlyGray: boolean = false;
    @Input() isInEditMode = true;
    // May implement this or not according to need;
    @Input() placeholder?: string;
    @Input() idField: string = 'id';
    @Input() nameField: string = 'name';
    @Input() autoFocus = false;
    @Input() emptyOption?: any;
    @Input() noData: string = 'No Data Found';
    @Input() autoOpen = false;
    @Input() renderTriggerAs: 'button' | 'field' | 'custom' = 'field';
    @Input() triggerTmpl?: TemplateRef<any>;
    @Input() automationLabel?: string;
    @Input() hideToolTip = false;
    @Input() iconTemplate?: TemplateRef<any>;
    @Input() public sortByField?: string;
    @Input() public sortOrder: SortOrder = 'ascending';
    @Input() public dynamicStyle: { [klass: string]: any } | null | undefined = undefined;
    @Input() public menuDynamicStyle: { [klass: string]: any } | null | undefined = undefined;
    @Input() public actionIconName?: IconName;
    @Input() public actionIconUrl?: string;

    // below outpute were added to enable server side search
    @Output() searchChanged = new EventEmitter<string>();
    @Output() listOptionClosed = new EventEmitter<void>();

    // @ViewChild('searchText') searchText: ElementRef<HTMLInputElement>;
    @ViewChild(MatMenuTrigger) matSelect: MatMenuTrigger = {} as MatMenuTrigger;

    // @ViewChild(CdkVirtualScrollViewport, { static: false }) viewPort: CdkVirtualScrollViewport;

    emptyValue = false;

    constructor(cd: ChangeDetectorRef, private keyboardService: KeyboardService, private svc: PFieldSingleSelectService) {
        super(cd);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.isRefreshSortNeeded(changes)) {
            this.sort();
        }
    }

    public sort(): void {
        // TODO: Clone the array for view only and sort the view array instead of mutating the referenced one we received
        this.list.sort(sortBy(this.sortByField as keyof ListItem, this.sortOrder));
    }

    getAutomationSuffix() {
        return !this.label ? this.automationLabel : this.label;
    }

    searchPredicate = (substring: string) => (item: any) =>
        item[this.idField] === this.emptyOption?.[this.idField] || _.toLower(item[this.nameField]).includes(_.trim(_.toLower(substring)));

    get classes(): object {
        return {
            'p-field-label--text': true
            /*[this.customClass]: true*/
        };
    }

    // openChange(ev) {
    //     if (ev) {
    //         this.viewPort.scrollToIndex(0);
    //         this.viewPort.checkViewportSize();
    //     }
    // }

    touch() {
        this.markAsTouched();
    }

    protected changeValue(value: number | string): void {
        this.markAsTouched();

        if (!this.isReadOnly) {
            this.value = value;
            this.matSelect?.closeMenu();
            this._onChange(this.value);
        }

        if (this.field?.customField) {
            this.svc.onSelectedValueChange({ fieldId: this.field.customFieldId, value: value });
        }
    }

    ngAfterViewInit(): void {
        // if (this.autoFocus) {
        //     this.searchText?.nativeElement.focus();
        // }
        if (this.autoOpen) {
            this.matSelect?.openMenu();
        }
    }

    getValue = (list: any) => (value: any) => {
        this.emptyValue = false;

        if ((!this.emptyOption && _.isNull(value)) || _.isUndefined(value)) {
            this.emptyValue = true;
            return '';
        }
        if (this.emptyOption?.[this.idField] === value || (value === 0 && !this.getListItemById(list, value))) {
            this.emptyValue = true;
            return this.emptyOption?.[this.nameField];
        } else {
            return this.getListItemById(list, value)?.[this.nameField] ?? value;
        }
    };

    ngOnInit(): void {
        if (this.supportCurrentIcon && this.list && this.list?.length > 0) {
            this.list.forEach((currentOption) => {
                if (currentOption.current) {
                    currentOption.iconName = 'map-pin';
                }
            });
        }

        this.filterValuesListByParentValue();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
        this.svc.onSelectedValueChange(null);
    }

    shouldDisplayIcon() {
        if (this.supportCurrentIcon && this.value) {
            const option = this.getListItemById(this.list, this.value);
            return option && option.current;
        }
        return false;
    }

    getListItemById(list: ListItem[], id: any): any {
        // we used '==' and not '===' because sometimes the value is string, sometimes integer. We want to compare regardless of type.
        return _.find(list, (listItem: any) => listItem[this.idField] == id);
    }

    onKeydownEvent(e: KeyboardEvent) {
        const code = e.keyCode;

        if (this.keyboardService.isDown(code) || this.keyboardService.isEnter(code)) {
            this.matSelect?.openMenu();
        }
    }

    private isRefreshSortNeeded(changes: SimpleChanges): boolean {
        return !!this.sortByField && ['list', 'sortByField', 'sortOrder'].some((k) => k in changes);
    }

    private filterValuesListByParentValue() {
        if (this.field?.customField && this.field.parentId) {
            this.svc.selectedValueChange$
                .pipe(
                    takeUntilDestroy(this),
                    tap((selectedValueChange: SelectedValueChangeInfo | null) => {
                        if (this.field && this.field.listOfValues && selectedValueChange?.fieldId === this.field.parentId) {
                            this.list = this.field.listOfValues.filter((val: ListItem) => val.parent === selectedValueChange?.value);
                            this.value = !this.list.find((val: ListItem) => val.id === this.value) ? null : this.value;
                            this.svc.onSelectedValueChange({ fieldId: this.field.customFieldId, value: this.value });
                            this._onChange(this.value);
                            this.cd.detectChanges();
                        }
                    })
                )
                .subscribe();
        }
    }
}

export interface ListItem {
    id: number | string;
    name: string;
    current?: boolean;
    /**
     * @deprecated Please use {@link leftIcon} or  {@link rightIcon} instead.
     */
    iconName?: IconName;
    /**
     * @deprecated Please use {@link leftIcon.showOnlyOnHover} or  {@link rightIcon.showOnlyOnHover} instead.
     */
    showIconOnHover?: boolean;
    /**
     * @deprecated Please use {@link leftIconClicked} or  {@link rightIconClicked} instead.
     */
    iconLink?: string;
    tooltipText?: string;
    disable?: boolean | (() => boolean);
    hasTrailingDivider?: boolean;
    onClick?: () => void;
    automationId?: string;
    parent?: string;
    hidden?: boolean;
    leftIcon?: Icon;
    rightIcon?: Icon;
}

export interface Icon {
    name: IconName;
    showOnlyOnHover: boolean;
    color?: ColorName;
}
